import { queryOptions } from "@tanstack/react-query";

import { api, API_URL } from "./api";
import { fetchFile } from "./download";
import type { Paginated } from "./users";

export type BolStatus = "draft" | "extracting" | "review" | "ready" | "exported";

export const STATUS_LABEL: Record<BolStatus, string> = {
  draft: "Draft",
  extracting: "Extracting",
  review: "In review",
  ready: "Ready to export",
  exported: "Exported",
};

export interface BolListItem {
  id: string;
  reference: string;
  manifest_reference: string;
  split_status: SplitStatus;
  description: string;
  status: BolStatus;
  extraction_is_stale: boolean;
  extraction_error: string;
  consignee_name: string | null;
  invoice_count: number;
  post_count: number;
  failed_invoices: number;
  unassigned_lines: number;
  missing_pdfs: number;
  /** Short reasons a person has to act on this bill; empty when it is flowing. */
  attention: string[];
  created_by_email: string;
  created_at: string;
  updated_at: string;
  exported_at: string | null;
}

export interface BolSummary {
  by_status: Record<BolStatus, number>;
  attention: number;
  total: number;
}

export interface InvoiceLine {
  id: string;
  invoice: string;
  post: string | null;
  position: number;
  page: number | null;
  /** 0 = top of the page, 100 = bottom; where the item number is stamped in the package. */
  position_percent: string | null;
  description: string;
  quantity: string;
  unit_price: string | null;
  amount: string;
  hs_code_hint: string;
  /** Supplier part/article number as printed, and the product-line code next to it (NAPA: BK, ATP). */
  part_number: string;
  product_line: string;
  commodity_code_suggested: string | null;
  commodity_code_suggested_code: string | null;
  commodity_code_suggested_description: string | null;
  /** "printed": HS code was on the invoice; "catalogue": the supplier's product list; "ai": proposed from the description. */
  suggestion_source: "printed" | "catalogue" | "ai" | "";
  suggestion_confidence: string | null;
  suggestion_reason: string;
  /** Why the last proposal run left the line without a code; "" when it worked. */
  suggestion_error: string;
  /** Dutch commercial description the model proposed; becomes the item description. */
  suggested_description_nl: string;
  is_excluded: boolean;
  stamp_override: StampOverride | null;
  source: "extracted" | "manual";
}

/** How costs and deductions are spread over the items. */
export type CostSplit = "value" | "colli" | "units";

export type ChargeKind =
  | "freight"
  | "insurance"
  | "origin_handling"
  | "inland_transport"
  | "documentation"
  | "packing"
  | "commission"
  | "foreign_tax"
  | "other"
  | "destination_handling"
  | "local"
  | "tax"
  | "discount"
  | "discount_excluded"
  | "ignore";

/** How a cost line enters the customs value; mirrors Charge.TREATMENT on the server. */
export type ChargeTreatment = "freight" | "insurance" | "other" | "deduction" | "excluded";

export const CHARGE_KINDS: { value: ChargeKind; label: string; treatment: ChargeTreatment }[] = [
  { value: "freight", label: "Freight to Aruba", treatment: "freight" },
  { value: "insurance", label: "Insurance", treatment: "insurance" },
  { value: "origin_handling", label: "Loading / origin THC", treatment: "other" },
  { value: "inland_transport", label: "Transport to port of export", treatment: "other" },
  { value: "documentation", label: "Documents / export declaration", treatment: "other" },
  { value: "packing", label: "Packing", treatment: "other" },
  { value: "commission", label: "Commission / brokerage", treatment: "other" },
  { value: "foreign_tax", label: "Foreign duty or tax, not refunded", treatment: "other" },
  { value: "other", label: "Other cost of the sale", treatment: "other" },
  { value: "discount", label: "Discount accepted by customs", treatment: "deduction" },
  { value: "destination_handling", label: "Unloading / destination THC", treatment: "excluded" },
  { value: "local", label: "Cost made in Aruba", treatment: "excluded" },
  { value: "tax", label: "Aruban duty, BBO or refunded tax", treatment: "excluded" },
  { value: "discount_excluded", label: "Discount not accepted", treatment: "excluded" },
  { value: "ignore", label: "Not a cost", treatment: "excluded" },
];

export const TREATMENT_LABEL: Record<ChargeTreatment, string> = {
  freight: "counts · freight",
  insurance: "counts · insurance",
  other: "counts · other costs",
  deduction: "deducted",
  excluded: "not in the value",
};

export interface Charge {
  id: string;
  /** Set for invoice charges, in the invoice's currency. */
  invoice: string | null;
  /** Set for transport-document charges, in the bill's currency. */
  bill_of_lading: string | null;
  description: string;
  amount: string;
  kind: ChargeKind;
  treatment: ChargeTreatment;
  page: number | null;
}

export interface Invoice {
  id: string;
  /** null while the invoice is registered but its PDF has not arrived. */
  document_url: string | null;
  supplier_name: string;
  invoice_number: string;
  invoice_date: string | null;
  currency: string | null;
  currency_code: string | null;
  exchange_rate_awg: string | null;
  total_amount: string | null;
  lines_total: string;
  page_count: number | null;
  /** Bundle pages this PDF was cut from; empty for an invoice registered without a PDF. */
  bundle_pages: number[];
  /** Cartons/packages printed on the invoice; sums into the bill total for FCL shipments. */
  package_count: number | null;
  extraction_status: "pending" | "running" | "classifying" | "done" | "failed";
  extraction_error: string;
  lines: InvoiceLine[];
  charges: Charge[];
  created_at: string;
}

/** What each invoice reading stage is called on screen. */
export const STAGE_LABEL: Record<Invoice["extraction_status"], string> = {
  pending: "Waiting",
  running: "Reading lines…",
  classifying: "Proposing codes…",
  done: "Done",
  failed: "Failed",
};

export interface Post {
  id: string;
  position: number;
  commodity_code: string | null;
  commodity_code_code: string | null;
  commodity_code_description: string | null;
  /** Heading > ... > leaf of the tariff; context for a leaf like "andere". */
  commodity_code_path: string | null;
  description: string;
  country_of_origin: string | null;
  package_count: number | null;
  gross_weight_kg: string | null;
  line_ids: string[];
  quantity: string;
  amount_awg: string;
}

export interface StatusEvent {
  id: string;
  from_status: BolStatus;
  to_status: BolStatus;
  user_email: string | null;
  note: string;
  created_at: string;
}

export interface Issue {
  code: string;
  message: string;
  field?: string;
  invoice?: string;
  post?: string;
}

/** One declaration item as customs will see it: apportioned costs, weight and packages. */
export interface DeclarationItem {
  post: string;
  position: number;
  commodity_code: string;
  tariff_description: string;
  commercial_description: string;
  country_of_origin_code: string;
  package_count: number;
  package_kind_code: string;
  quantity: string;
  gross_weight_kg: string;
  invoice_foreign: string;
  invoice_awg: string;
  freight_foreign: string;
  insurance_foreign: string;
  other_foreign: string;
  freight_awg: string;
  insurance_awg: string;
  other_awg: string;
  total_cost_awg: string;
  cif_awg: string;
  statistical_value: string;
  duty_percent: string | null;
  estimated_duty_awg: string | null;
}

/** Header valuation computed by the server; the same figures go into the XML. */
export interface Declaration {
  currency_code: string;
  rate: string;
  invoice_foreign: string;
  invoice_awg: string;
  freight_foreign: string;
  insurance_foreign: string;
  other_foreign: string;
  freight_awg: string;
  insurance_awg: string;
  other_awg: string;
  deduction_foreign: string;
  deduction_awg: string;
  cost_split: CostSplit;
  total_cost_awg: string;
  cif_awg: string;
  total_packages: number;
  total_weight_kg: string;
  estimated_duty_awg: string | null;
  items: DeclarationItem[];
}

export type LineAnchor =
  | { page: number; box: [number, number, number, number]; estimate: false; manual?: boolean }
  | { page: number; hidden: true; estimate: false }
  | { page: number; y: number; estimate: true };

/**
 * Where the reviewer put a line's stamp: a box in fractions of the displayed page
 * (origin top-left, y down), or no stamp at all. null = automatic (OCR).
 */
export type StampOverride =
  { page: number; box: [number, number, number, number] } | { hidden: true };

export interface LineAnchors {
  page: number;
  /** Clockwise degrees the package turns this page to read upright; rows run vertically on screen when 90 or 270. */
  turn: number;
  anchors: Record<string, LineAnchor>;
}

export interface SplitSuggestion {
  transport_pages: number[];
  invoice_groups: number[][];
  other_pages: number[];
  reasons: Record<string, string>;
  /** The bundle this proposal was made for; a newer upload gets its own run. */
  bundle_version?: number;
}

/**
 * Where the uploaded pages are on their way to transport document + invoices.
 * none: no uploads. applied: the reviewer confirmed the split. Everything else blocks
 * reading and status changes until resolved.
 */
export type SplitStatus = "none" | "pending" | "running" | "suggested" | "applied" | "failed";

/** A file as it was uploaded, kept unchanged, with where its pages sit in the bundle. */
export interface Upload {
  id: string;
  filename: string;
  page_count: number;
  /** 1-based page of the bundle this file starts at. */
  first_page: number;
  position: number;
  url: string;
  uploaded_by_email: string;
  created_at: string;
}

/** True while pages are being sorted or a proposal waits for the reviewer. */
export function splitUnresolved(status: SplitStatus): boolean {
  return status !== "none" && status !== "applied";
}

/** The upload a bundle page belongs to, for "from scan.pdf, p. 3-5" captions. */
export function uploadForPage(uploads: Upload[], page: number): Upload | undefined {
  return uploads.find((u) => page >= u.first_page && page < u.first_page + u.page_count);
}

/** What goes when an upload is removed: the invoices cut from it and the review work on them. */
export function removalImpact(
  bol: Pick<BolDetail, "invoices" | "transport_pages">,
  upload: Upload,
): { invoices: Invoice[]; lines: number; items: number; transport: boolean } {
  const inFile = (p: number) => p >= upload.first_page && p < upload.first_page + upload.page_count;
  const invoices = bol.invoices.filter((i) => i.bundle_pages.some(inFile));
  const lines = invoices.reduce((n, i) => n + i.lines.length, 0);
  const items = new Set(invoices.flatMap((i) => i.lines.map((l) => l.post).filter(Boolean))).size;
  return { invoices, lines, items, transport: bol.transport_pages.some(inFile) };
}

/** Consecutive runs in a sorted list of numbers: [3,4,5,8] → "3–5, 8". */
function ranges(pages: number[]): string {
  const out: string[] = [];
  let start = pages[0];
  let prev = pages[0];
  for (const p of [...pages.slice(1), NaN]) {
    if (p === prev + 1) {
      prev = p;
      continue;
    }
    out.push(start === prev ? `${start}` : `${start}–${prev}`);
    start = prev = p;
  }
  return out.join(", ");
}

/**
 * Where a cut document came from, in the uploader's terms: the file name and the
 * pages inside that file. "from scan.pdf, p. 3–5" or, across files,
 * "from scan.pdf, p. 6; inv.pdf, p. 1–2". Empty when the pages are unknown
 * (invoices from before uploads were kept, or an invoice without a PDF).
 */
export function describeOrigin(uploads: Upload[], bundlePages: number[]): string {
  if (bundlePages.length === 0 || uploads.length === 0) return "";
  const byUpload = new Map<Upload, number[]>();
  for (const page of [...bundlePages].sort((a, b) => a - b)) {
    const upload = uploadForPage(uploads, page);
    if (!upload) continue;
    byUpload.set(upload, [...(byUpload.get(upload) ?? []), page - upload.first_page + 1]);
  }
  const parts = [...byUpload].map(([u, pages]) => {
    const whole = pages.length === u.page_count;
    return whole ? `${u.filename} (whole file)` : `${u.filename}, p. ${ranges(pages)}`;
  });
  return parts.length ? `from ${parts.join("; ")}` : "";
}

export interface BolDetail {
  id: string;
  reference: string;
  description: string;
  status: BolStatus;
  is_editable: boolean;
  can_extract: boolean;
  /** Still "extracting" long after it started: the job died or no worker is running. */
  extraction_is_stale: boolean;
  can_split: boolean;
  /** The page sorter may be (re)run: not while one runs, unless it is stuck. */
  can_suggest_split: boolean;
  can_export: boolean;
  issues: Issue[];
  warnings: Issue[];
  declaration: Declaration;
  document_url: string | null;
  bundle_document_url: string | null;
  /** Bundle pages the transport document was cut from. */
  transport_pages: number[];
  /** Bundle pages the reviewer left out; proposals and re-splits keep them out. */
  skipped_pages: number[];
  uploads: Upload[];
  split_status: SplitStatus;
  split_is_stale: boolean;
  split_error: string;
  split_suggestion: SplitSuggestion | null;
  split_started_at: string | null;
  split_finished_at: string | null;
  extracted: Record<string, unknown> | null;
  extraction_error: string;
  extraction_finished_at: string | null;
  manifest_reference: string;
  customs_manifest_number: string;
  declaration_type: string | null;
  registration_number: string;
  exporter: string | null;
  consignee: string | null;
  office_of_entry: string | null;
  office_of_destination: string | null;
  country_of_origin: string | null;
  country_of_export: string | null;
  country_of_destination: string | null;
  nature_of_transaction: string | null;
  inco_term: string | null;
  delivery_place: string;
  transport_mode: string | null;
  inland_transport_mode: string | null;
  vessel: string | null;
  voyage_number: string;
  container_number: string;
  is_container: boolean;
  place_of_loading: string;
  port_of_unloading: string | null;
  location_of_goods: string | null;
  warehouse: string | null;
  package_count: number | null;
  package_type: string | null;
  gross_weight_kg: string | null;
  currency: string | null;
  exchange_rate_awg: string | null;
  freight_amount: string;
  insurance_amount: string;
  insurance_is_estimated: boolean;
  other_costs_amount: string;
  deduction_amount: string;
  cost_split: CostSplit;
  /** Cost lines on the transport document; invoice cost lines sit on each invoice. */
  charges: Charge[];
  invoice_total_awg: string;
  invoices: Invoice[];
  posts: Post[];
  status_events: StatusEvent[];
  created_by_email: string;
  updated_by_email: string;
  exported_at: string | null;
  exported_by_email: string | null;
  exported_document_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface BolListParams {
  search?: string;
  status?: BolStatus | "";
  attention?: boolean;
  page?: number;
}

export function bolsQuery(params: BolListParams) {
  const qs = new URLSearchParams();
  if (params.search) qs.set("search", params.search);
  if (params.status) qs.set("status", params.status);
  if (params.attention) qs.set("attention", "1");
  if (params.page && params.page > 1) qs.set("page", String(params.page));
  const suffix = qs.toString() ? `?${qs}` : "";
  return queryOptions({
    queryKey: ["bols", params],
    queryFn: () => api<Paginated<BolListItem>>(`/bols/${suffix}`),
    placeholderData: (previous) => previous,
    // keep the queue live while something is being read
    refetchInterval: (query) =>
      query.state.data?.results.some((b) => b.status === "extracting") ? 5000 : false,
  });
}

export const bolSummaryQuery = queryOptions({
  queryKey: ["bols", "summary"],
  queryFn: () => api<BolSummary>("/bols/summary/"),
  refetchInterval: 15000,
});

export function bolQuery(id: string) {
  return queryOptions({
    queryKey: ["bols", id],
    queryFn: () => api<BolDetail>(`/bols/${id}/`),
    // While the worker sorts pages or reads documents, keep the page live.
    refetchInterval: (query) => {
      const bol = query.state.data;
      if (!bol) return false;
      const sorting = bol.split_status === "pending" || bol.split_status === "running";
      return bol.status === "extracting" || sorting ? 3000 : false;
    },
  });
}

export interface CreateBolInput {
  reference: string;
  manifestReference: string;
  description: string;
  /** Any PDFs in any mix; their pages are sorted after upload. */
  documents: File[];
}

function documentsForm(documents: File[], form = new FormData()): FormData {
  for (const file of documents) form.append("documents", file);
  return form;
}

/** Header fields a reviewer can change; everything is optional in a PATCH. */
export type BolHeaderPatch = Partial<
  Pick<
    BolDetail,
    | "reference"
    | "manifest_reference"
    | "customs_manifest_number"
    | "description"
    | "declaration_type"
    | "registration_number"
    | "exporter"
    | "consignee"
    | "office_of_entry"
    | "office_of_destination"
    | "country_of_origin"
    | "country_of_export"
    | "country_of_destination"
    | "nature_of_transaction"
    | "inco_term"
    | "delivery_place"
    | "transport_mode"
    | "inland_transport_mode"
    | "vessel"
    | "voyage_number"
    | "container_number"
    | "is_container"
    | "place_of_loading"
    | "port_of_unloading"
    | "location_of_goods"
    | "warehouse"
    | "package_count"
    | "package_type"
    | "gross_weight_kg"
    | "currency"
    | "exchange_rate_awg"
    | "freight_amount"
    | "insurance_amount"
    | "insurance_is_estimated"
    | "other_costs_amount"
    | "deduction_amount"
    | "cost_split"
  >
>;

export type InvoicePatch = Partial<
  Pick<
    Invoice,
    | "supplier_name"
    | "invoice_number"
    | "invoice_date"
    | "currency"
    | "exchange_rate_awg"
    | "total_amount"
  >
>;
export type LinePatch = Partial<
  Pick<
    InvoiceLine,
    | "description"
    | "quantity"
    | "unit_price"
    | "amount"
    | "page"
    | "hs_code_hint"
    | "part_number"
    | "product_line"
    | "is_excluded"
    | "post"
    | "stamp_override"
  >
>;
export type ChargePatch = Partial<Pick<Charge, "description" | "amount" | "kind" | "page">>;
export type PostPatch = Partial<
  Pick<
    Post,
    "commodity_code" | "description" | "country_of_origin" | "package_count" | "gross_weight_kg"
  >
> & {
  line_ids?: string[];
  add_line_ids?: string[];
  remove_line_ids?: string[];
};

export const bolsApi = {
  create: (input: CreateBolInput) => {
    const form = new FormData();
    form.set("reference", input.reference);
    form.set("manifest_reference", input.manifestReference);
    form.set("description", input.description);
    documentsForm(input.documents, form);
    return api<BolDetail>("/bols/", { method: "POST", body: form });
  },
  /** Upload more files to an existing bill; all pages are sorted again. */
  addDocuments: (id: string, documents: File[]) =>
    api<BolDetail>(`/bols/${id}/documents/`, { method: "POST", body: documentsForm(documents) }),
  /** Remove one uploaded file; the remaining pages are sorted again. */
  removeUpload: (id: string, uploadId: string) =>
    api<BolDetail>(`/bols/${id}/uploads/${uploadId}/`, { method: "DELETE" }),
  update: (id: string, patch: BolHeaderPatch) =>
    api<BolDetail>(`/bols/${id}/`, { method: "PATCH", body: patch }),
  extract: (id: string) => api<BolDetail>(`/bols/${id}/extract/`, { method: "POST" }),
  /** Split the bundle; reading of the new files starts right away unless `extract` is false. */
  split: (
    id: string,
    plan: { transport_pages: number[]; invoice_groups: number[][]; extract?: boolean },
  ) => api<BolDetail>(`/bols/${id}/split/`, { method: "POST", body: plan }),
  /** (Re)run the page sorter in the background; also the retry after a failure. */
  suggestSplit: (id: string) => api<BolDetail>(`/bols/${id}/split/suggest/`, { method: "POST" }),
  proposePosts: (id: string) => api<BolDetail>(`/bols/${id}/posts/propose/`, { method: "POST" }),
  /** Fold items that share a commodity code into one item each. */
  mergePosts: (id: string) => api<BolDetail>(`/bols/${id}/posts/merge/`, { method: "POST" }),
  /**
   * "unassigned": propose for lines without a code; "uncertain": re-ask for amber lines and
   * regroup; `lineIds`: retry exactly these unassigned lines (e.g. the ones that failed).
   */
  classifyLines: (
    id: string,
    scope: "unassigned" | "uncertain" = "unassigned",
    lineIds?: string[],
  ) =>
    api<BolDetail>(`/bols/${id}/classify/`, {
      method: "POST",
      body: lineIds ? { line_ids: lineIds } : { scope },
    }),
  createPost: (id: string, body: PostPatch) =>
    api<Post>(`/bols/${id}/posts/`, { method: "POST", body }),
  updatePost: (postId: string, body: PostPatch) =>
    api<Post>(`/posts/${postId}/`, { method: "PATCH", body }),
  deletePost: (postId: string) => api<void>(`/posts/${postId}/`, { method: "DELETE" }),
  updateInvoice: (invoiceId: string, body: InvoicePatch) =>
    api<Invoice>(`/invoices/${invoiceId}/`, { method: "PATCH", body }),
  deleteInvoice: (invoiceId: string) => api<void>(`/invoices/${invoiceId}/`, { method: "DELETE" }),
  /** Drafts by anyone, anything by an admin; files, invoices, lines and items go with it. */
  deleteBol: (id: string) => api<void>(`/bols/${id}/`, { method: "DELETE" }),
  /** Where the lines of one invoice page sit on the scan, for the viewer's highlight. */
  lineAnchors: (invoiceId: string, page: number) =>
    api<LineAnchors>(`/invoices/${invoiceId}/anchors/?page=${page}`),
  addLine: (invoiceId: string, body: LinePatch & { description: string; amount: string }) =>
    api<InvoiceLine>(`/invoices/${invoiceId}/lines/`, { method: "POST", body }),
  updateLine: (lineId: string, body: LinePatch) =>
    api<InvoiceLine>(`/invoice-lines/${lineId}/`, { method: "PATCH", body }),
  deleteLine: (lineId: string) => api<void>(`/invoice-lines/${lineId}/`, { method: "DELETE" }),
  addCharge: (invoiceId: string, body: ChargePatch & { description: string; amount: string }) =>
    api<Charge>(`/invoices/${invoiceId}/charges/`, { method: "POST", body }),
  addBolCharge: (bolId: string, body: ChargePatch & { description: string; amount: string }) =>
    api<Charge>(`/bols/${bolId}/charges/`, { method: "POST", body }),
  updateCharge: (chargeId: string, body: ChargePatch) =>
    api<Charge>(`/charges/${chargeId}/`, { method: "PATCH", body }),
  deleteCharge: (chargeId: string) => api<void>(`/charges/${chargeId}/`, { method: "DELETE" }),
  setStatus: (id: string, status: BolStatus, note = "") =>
    api<BolDetail>(`/bols/${id}/status/`, { method: "POST", body: { status, note } }),
  remove: (id: string) => api<void>(`/bols/${id}/`, { method: "DELETE" }),
  /** Register an invoice that has not arrived yet (metadata only); PDFs go through `addDocuments`. */
  addInvoice: (bolId: string, input: InvoicePatch) =>
    api<Invoice>(`/bols/${bolId}/invoices/`, { method: "POST", body: input }),
  export: (id: string) => api<BolDetail>(`/bols/${id}/export/`, { method: "POST" }),
  /** The XML as it stands now, without exporting. */
  previewXml: (id: string) => api<string>(`/bols/${id}/export/`),
  downloadUrl: (id: string) => `${API_URL}/api/bols/${id}/export/download/`,
  /** Zip with the XML, the transport document and the invoices merged, to attach in ASYCUDA. */
  packageUrl: (id: string) => `${API_URL}/api/bols/${id}/export/package/`,
  /** The ASYCUDA package as a file; building it can take a while, so callers show progress. */
  downloadPackage: (id: string) => fetchFile(bolsApi.packageUrl(id), "ASYCUDA_PACKAGE.zip"),
  /** The files as they were uploaded: one PDF as itself, several as a zip. */
  downloadUploads: (id: string) =>
    fetchFile(`${API_URL}/api/bols/${id}/uploads/download/`, "UPLOADS.zip"),
};

export function formatMoney(value: string | null | undefined, currency?: string | null): string {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return `${number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;
}
